import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, homeForRole } from "../auth/AuthContext";
import { Logo } from "../components/Navbar";
import { fmtErr } from "../lib/api";
import { Field, inputCls } from "../portal/ui";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("customer");
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const user = await register({ ...form, role });
      toast.success(
        role === "supplier" ? "Account created — pending review" : "Account created",
        { description: role === "supplier" ? "Ajemi will review your supplier profile before activation." : "Welcome to Ajemi." }
      );
      navigate(homeForRole(user.role));
    } catch (err) {
      toast.error("Sign up failed", { description: fmtErr(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white flex flex-col items-center justify-center px-6 py-12" data-testid="signup-page">
      <Link to="/" className="mb-10">
        <Logo />
      </Link>
      <div className="glass-panel rounded-2xl p-8 md:p-10 w-full max-w-md">
        <h1 className="font-outfit text-3xl font-extralight tracking-tight">Create <span className="text-gold">Account</span></h1>

        <div className="grid grid-cols-2 gap-3 mt-8" data-testid="signup-role-toggle">
          {[
            { value: "customer", label: "Customer", hint: "I need to procure energy products" },
            { value: "supplier", label: "Supplier", hint: "I supply petroleum products" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-testid={`signup-role-${opt.value}`}
              onClick={() => setRole(opt.value)}
              className={`text-left rounded-xl border px-4 py-4 transition-all duration-300 ${
                role === opt.value ? "border-gold/60 bg-gold/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"
              }`}
            >
              <span className={`block font-outfit text-sm ${role === opt.value ? "text-gold" : "text-white/80"}`}>{opt.label}</span>
              <span className="block text-[11px] text-white/40 mt-1">{opt.hint}</span>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-5" data-testid="signup-form">
          <Field label="Company Name" required>
            <input required value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder="Your company" data-testid="signup-company-input" />
          </Field>
          <Field label="Contact Name" required>
            <input required value={form.contact_name} onChange={set("contact_name")} className={inputCls} placeholder="Full name" data-testid="signup-contact-input" />
          </Field>
          <Field label="Email" required>
            <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="name@company.com" data-testid="signup-email-input" />
          </Field>
          <Field label="Phone">
            <input type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+234 ..." data-testid="signup-phone-input" />
          </Field>
          <Field label="Password" required>
            <input required type="password" minLength={8} value={form.password} onChange={set("password")} className={inputCls} placeholder="Minimum 8 characters" data-testid="signup-password-input" />
          </Field>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gold text-ink font-outfit font-medium rounded-full py-3.5 hover:bg-goldbright hover:shadow-[0_0_32px_rgba(217,164,65,0.4)] transition-all disabled:opacity-60"
            data-testid="signup-submit-btn"
          >
            {loading ? "Creating…" : role === "supplier" ? "Apply as Supplier" : "Create Account"}
          </button>
          {role === "supplier" && (
            <p className="text-xs text-white/40 leading-relaxed" data-testid="supplier-review-note">
              Supplier accounts are reviewed by Ajemi before activation. You can complete your
              company profile immediately after signing up.
            </p>
          )}
          <p className="text-sm text-white/45 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-gold hover:text-goldbright transition-colors" data-testid="goto-login-link">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
